import { decodeFilters, encodeFilters } from '@/lib/filterUtils'
import { DATE_ONLY_TYPES, DATE_TIME_TYPES } from '@/utils/clickhouseTypes'
import { z } from 'zod'
import { buildConditionId } from './groupedFilterFields'
import { buildGroupId } from './groupedFilterState'
import { compactGroups } from './groupedFilterStorage'
import type { FilterGroup, FilterState } from './types'

/** The `ClickhouseToTSTypes` union, spelled as a value so the schema cannot drift from the type. */
const COLUMN_TYPES = [
	'int',
	'uint',
	'float',
	'string',
	'boolean',
	'null',
	...DATE_TIME_TYPES,
	...DATE_ONLY_TYPES,
] as const

const conditionSchema = z
	.object({
		id: z.string().optional(),
		field: z.string(),
		operator: z.string(),
		operatorLabel: z.string().optional(),
		value: z
			.union([z.array(z.union([z.string(), z.number(), z.null()])), z.string(), z.number(), z.null()])
			.default([]),
		type: z.enum(['metadata', 'state']).default('metadata'),
		isNullOperator: z.boolean().default(false),
		// Non-optional on FilterState, and the query builder reads it — but a legacy or hand-written link may
		// omit it or carry a type this build does not know. `catch` keeps such a condition instead of dropping
		// it, landing on the same 'string' `buildCondition` gives a field with no clickhouse type. Time windows
		// survive it: `isRelativeTimeCondition` still recognises `heartbeat` by field name.
		columnType: z.enum(COLUMN_TYPES).catch('string'),
	})
	.passthrough()

const groupSchema = z
	.object({
		id: z.string().optional(),
		operator: z.enum(['and', 'or']).default('and'),
		joiner: z.enum(['and', 'or']).default('or'),
		conditions: z.array(z.unknown()),
	})
	.passthrough()

/**
 * Shared `?groups` links already exist, so restore is lenient by construction: an unparseable condition
 * is dropped on its own and a group left empty goes with it, rather than the whole bar emptying.
 * `passthrough` keeps `fieldLabel` / `valueLabels` / `derivedExpression`, which the schema does not model
 * but the builder and chips read.
 */
export function parseGroupsParam(param: string | undefined): FilterGroup[] {
	const decoded = decodeFilters<unknown>(param)
	if (decoded === undefined) {
		return []
	}
	if (!Array.isArray(decoded)) {
		console.warn('Ignoring malformed `groups` URL parameter')
		return []
	}
	return parseGroups(decoded)
}

/** The same lenient read for groups kept anywhere else, such as a dashboard's saved scope. */
export function parseGroups(decoded: unknown[]): FilterGroup[] {
	const groups: FilterGroup[] = []
	for (const raw of decoded) {
		const parsedGroup = groupSchema.safeParse(raw)
		if (!parsedGroup.success) {
			continue
		}
		const seen = new Set<string>()
		const conditions: FilterState[] = []
		for (const rawCondition of parsedGroup.data.conditions) {
			const parsed = conditionSchema.safeParse(rawCondition)
			if (!parsed.success) {
				continue
			}
			const id = parsed.data.id && !seen.has(parsed.data.id) ? parsed.data.id : buildConditionId()
			seen.add(id)
			conditions.push({ ...(parsed.data as unknown as FilterState), id })
		}
		if (conditions.length === 0) {
			continue
		}
		// Built field by field, so a link can never carry `locked`: the host that pins a group restores it.
		groups.push({
			id: parsedGroup.data.id ?? buildGroupId(),
			operator: parsedGroup.data.operator,
			joiner: parsedGroup.data.joiner,
			conditions,
		})
	}
	return groups
}

/**
 * Compacted on the way out: ids are rebuilt on read, and a label equal to the value it labels says
 * nothing. Roughly a third off a shared link, which matters where one is pasted into a mail client
 * that wraps or truncates past a length.
 *
 * Nothing downstream needs the ids a link used to carry. The one reader that did is the pin in
 * `useGroupedFilterHost`, which drops a restored copy of a pinned group by id; it still catches a link
 * written by an older build, and the case it guards cannot arise from one written by this one, since a
 * locked group never reaches here.
 */
export const encodeGroupsParam = (groups: FilterGroup[]): string | undefined => {
	const own = groups.filter(group => !group.locked)
	return own.length > 0 ? encodeFilters(compactGroups(own)) : undefined
}
